import { useEffect, useRef, useState } from "react";
import axios from "axios";
import LatestStories from "./LatestStories";
import { getMediaBaseUrl } from "../utils/backendUrl";

const getYoutubeId = (url) => {
  if (!url) return "";
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : "";
};

const RightSidebar = () => {
  const [ads, setAds] = useState([]); // { id, kind: 'youtube'|'file', src }
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ytReady, setYtReady] = useState(false);
  const playerRef = useRef(null);
  const youtubeBoxRef = useRef(null);

  const baseUrl = getMediaBaseUrl();

  // Load YouTube IFrame API once
  useEffect(() => {
    const loadYtApi = () => {
      if (window.YT && window.YT.Player) {
        setYtReady(true);
        return;
      }
      if (document.getElementById("youtube-iframe-api")) {
        const check = () =>
          window.YT && window.YT.Player ? setYtReady(true) : setTimeout(check, 200);
        check();
        return;
      }
      const tag = document.createElement("script");
      tag.id = "youtube-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = () => setYtReady(true);
    };
    loadYtApi();
  }, []);

  // Fetch all video ads from backend
  useEffect(() => {
    const fetchVideoAds = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}message`);
        const items = Array.isArray(res?.data) ? res.data : [];

        const videoAds = items
          .filter((ad) => ad.type === "video" && ad.is_active)
          .map((ad) => {
            if (ad.video_url) {
              const id = getYoutubeId(ad.video_url);
              if (id) return { id: ad.id || id, kind: "youtube", src: id };
            }
            if (ad.video) {
              return {
                id: ad.id,
                kind: "file",
                src: `${baseUrl}/uploads/messages/${ad.video}`,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (videoAds.length > 0) {
          setAds(videoAds);
        }
      } catch (err) {
        console.error("Failed to fetch video ads", err);
      }
    };

    fetchVideoAds();
  }, []);

  const current = ads[currentIndex];
  const isSingle = ads.length === 1;

  // Auto-advance to next ad
  const handleEnded = () => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  };

  // Create/destroy YouTube player for current youtube ad
  useEffect(() => {
    if (!ytReady || !current || current.kind !== "youtube") return;
    if (!youtubeBoxRef.current) return;

    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    playerRef.current = new window.YT.Player(youtubeBoxRef.current, {
      videoId: current.src,
      playerVars: {
        autoplay: 1,
        mute: 1,
        playsinline: 1,
        rel: 0,
        controls: 0,
        disablekb: 1,
        loop: isSingle ? 1 : 0,
        playlist: isSingle ? current.src : undefined,
      },
      events: {
        onStateChange: (e) => {
          if (!isSingle && e.data === window.YT.PlayerState.ENDED) {
            handleEnded();
          }
        },
      },
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [ytReady, currentIndex, current, isSingle]);

  // Reset index if ads list shrinks
  useEffect(() => {
    if (ads.length > 0 && currentIndex >= ads.length) {
      setCurrentIndex(0);
    }
  }, [ads, currentIndex]);

  return (
    <div className="space-y-6 sticky top-[60px]">
      {/* 🔹 LATEST STORIES */}
      <LatestStories />

      {/* 🔹 VIDEO ADVERTISEMENT SECTION (Same as Home) */}
      <div className="bg-white border-gray-200 rounded-2xl border shadow-sm overflow-hidden relative group">
        {/* Ad Icon top-right (same style as LeftSidebar) */}
        <div className="absolute top-0 right-0 bg-black/50 text-white text-[9px] uppercase px-2 py-0.5 font-bold z-20 tracking-widest">Ad</div>

        <div className="w-full h-[250px] bg-brand-red overflow-hidden relative shadow-inner">
          {current && current.kind === "youtube" && (
            <div ref={youtubeBoxRef} className="absolute inset-0 w-full h-full"></div>
          )}

          {current && current.kind === "file" && (
            <video
              key={current.id}
              className="absolute inset-0 w-full h-full object-cover"
              src={current.src}
              autoPlay
              muted
              playsInline
              loop={isSingle}
              onEnded={handleEnded}
            ></video>
          )}

          {!current && (
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/L7fIF53G_P4?autoplay=1&mute=1&loop=1&playlist=L7fIF53G_P4"
              title="Video Advertisement"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          )}
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;